import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../../config';
import './MobileCardRecognition.css';

const MobileCardRecognition = ({
  addToCart,
  userSettings,
  exchangeRates,
  calculatePrice,
  allowedConditions,
  onBack
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [recognizedCards, setRecognizedCards] = useState([]);
  const [error, setError] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);

  // Debug camera state changes
  useEffect(() => {
    console.log('Mobile: cameraActive state changed to:', cameraActive);
  }, [cameraActive]);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cardPrices, setCardPrices] = useState({});
  const priceRequests = useRef({});
  const priceCalculator = useRef(calculatePrice);
  priceCalculator.current = calculatePrice;
  useEffect(() => () => { priceRequests.current = {}; }, []);

  // Only the latest selection may publish a quote; API responses can finish out of order.
  const updateCardPrice = useCallback(async (card, condition, printing, language) => {
    const request = {};
    priceRequests.current[card._id] = request;
    setCardPrices(prev => ({ ...prev, [card._id]: { contactForPrice: true } }));
    try {
      const quote = await priceCalculator.current(card, condition, printing, language);
      if (priceRequests.current[card._id] === request) {
        setCardPrices(prev => ({ ...prev, [card._id]: quote }));
      }
    } catch (error) {
      if (priceRequests.current[card._id] === request) {
        setCardPrices(prev => ({ ...prev, [card._id]: { contactForPrice: true } }));
      }
    }
  }, []);

  const [selectedConditions, setSelectedConditions] = useState({});
  const [selectedPrintings, setSelectedPrintings] = useState({});
  const [selectedLanguages, setSelectedLanguages] = useState({});
  const [showInstructions, setShowInstructions] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  // Hide instructions on first interaction
  const handleFirstInteraction = useCallback(() => {
    if (!hasInteracted) {
      setHasInteracted(true);
      setShowInstructions(false);
    }
  }, [hasInteracted]);

  // Start camera
  const startCamera = useCallback(async () => {
    handleFirstInteraction();
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Use back camera on mobile
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });

      console.log('Mobile: Camera stream obtained:', stream);

      // Set camera active first to render the video element
      setCameraActive(true);
      console.log('Mobile: Setting cameraActive to true');

      // Wait for video element to be rendered, then set the stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          console.log('Mobile: Camera stream set to video element');
          console.log('Mobile: videoRef.current exists:', !!videoRef.current);

          // Ensure video plays
          videoRef.current.onloadedmetadata = () => {
            console.log('Mobile: Video metadata loaded, playing video');
            console.log('Mobile: Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
            videoRef.current.play().catch(e => console.error('Mobile: Error playing video:', e));
          };
        } else {
          console.error('Mobile: videoRef.current is still null after timeout!');
        }
      }, 100); // Small delay to allow React to render the video element
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions or use file upload instead.');
    }
  }, [handleFirstInteraction]);

  // Stop camera
  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
  }, []);

  // Capture photo from camera with cropping and white border
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    console.log('Starting photo capture...');

    // Get video dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;

    console.log('Video dimensions:', videoWidth, 'x', videoHeight);

    // Calculate the guide frame dimensions relative to video
    // Guide frame is 280x392px on screen, need to map to video coordinates
    const containerWidth = video.offsetWidth;
    const containerHeight = video.offsetHeight;

    console.log('Container dimensions:', containerWidth, 'x', containerHeight);

    // Calculate scale factors
    const scaleX = videoWidth / containerWidth;
    const scaleY = videoHeight / containerHeight;

    console.log('Scale factors:', scaleX, scaleY);

    // Guide frame dimensions (280x392 on screen)
    const guideWidth = 280 * scaleX;
    const guideHeight = 392 * scaleY;

    console.log('Guide dimensions:', guideWidth, 'x', guideHeight);

    // Center the crop area
    const cropX = (videoWidth - guideWidth) / 2;
    const cropY = (videoHeight - guideHeight) / 2;

    console.log('Crop position:', cropX, cropY);

    // Validate crop dimensions
    if (cropX < 0 || cropY < 0 || guideWidth <= 0 || guideHeight <= 0 ||
        cropX + guideWidth > videoWidth || cropY + guideHeight > videoHeight) {
      console.warn('Invalid crop dimensions, using full frame');
      // Fallback to full frame capture
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      context.drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          setCapturedImage(imageUrl);
          stopCamera();
          recognizeCard(blob);
        }
      }, 'image/jpeg', 0.9);
      return;
    }

    // Create a temporary canvas for cropping
    const tempCanvas = document.createElement('canvas');
    const tempContext = tempCanvas.getContext('2d');

    // Set temp canvas to cropped dimensions
    tempCanvas.width = guideWidth;
    tempCanvas.height = guideHeight;

    // Draw the cropped video frame
    tempContext.drawImage(
      video,
      cropX, cropY, guideWidth, guideHeight,  // Source rectangle
      0, 0, guideWidth, guideHeight           // Destination rectangle
    );

    // Create final canvas with white border (2mm ≈ 8px at typical DPI)
    const borderSize = 8;
    const finalWidth = guideWidth + (borderSize * 2);
    const finalHeight = guideHeight + (borderSize * 2);

    console.log('Final image dimensions:', finalWidth, 'x', finalHeight);

    canvas.width = finalWidth;
    canvas.height = finalHeight;

    // Fill with white background
    context.fillStyle = 'white';
    context.fillRect(0, 0, finalWidth, finalHeight);

    // Draw the cropped image centered with border
    context.drawImage(tempCanvas, borderSize, borderSize);

    console.log('Image processing complete, sending for recognition');

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        console.log('Blob created, size:', blob.size, 'bytes');
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);

        // Close the camera viewport after capture
        stopCamera();

        recognizeCard(blob);
      } else {
        console.error('Failed to create blob from canvas');
      }
    }, 'image/jpeg', 0.9); // Higher quality for recognition
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback((event) => {
    handleFirstInteraction();
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setCapturedImage(imageUrl);
      recognizeCard(file);
    }
  }, [handleFirstInteraction]);

  // Recognize card from image
  const recognizeCard = useCallback(async (imageBlob) => {
    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', imageBlob);

      console.log('Sending card recognition request...');
      const response = await fetch(`${BACKEND_URL}/recognize-card`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Card recognition response:', data);

      if (data.success && data.productId) {
        console.log('Received productId from backend:', data.productId);

        // Use EXACT same process as manual search but stay on card recognition page
        await searchForCardByProductId(data.productId);

      } else {
        const errorMessage = data.error || 'No cards recognized in the image. Please try again with a clearer photo.';
        console.log('Recognition failed:', errorMessage);
        setError(errorMessage);
        setRecognizedCards([]);
        // Keep captured image visible so user can see what was processed
      }
    } catch (err) {
      console.error('Error recognizing card:', err);
      setError('Failed to recognize card. Please check your connection and try again.');
      setRecognizedCards([]);
      // Keep captured image visible so user can see what was processed
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Search for card using productId - search across ALL games without game filter
  const searchForCardByProductId = useCallback(async (productId) => {
    try {
      const url = new URL(`${BACKEND_URL}/catalog`);
      const showNotBuying = localStorage.getItem('showNotBuying') !== null ?
        JSON.parse(localStorage.getItem('showNotBuying')) : true;

      // DO NOT include game parameter - let backend search across all games
      const searchParams = {
        page: '1',
        limit: '25',
        productId: productId.toString(),
        username: userSettings?.username || 'admintcg',
        showBulk: 'true',
        showNotBuying: showNotBuying.toString()
      };
      
      url.search = new URLSearchParams(searchParams).toString();
      console.log('Mobile card recognition fetching from catalog URL (no game filter):', url.toString());

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const catalogData = await response.json();
      console.log('Mobile card recognition received catalog data:', catalogData);

      if (catalogData.items && catalogData.items.length > 0) {
        const foundCard = catalogData.items[0];
        
        // Check if the user has enabled purchasing for this game
        const cardGame = foundCard.gameName;
        const enabledGames = userSettings?.enabledGames || [];
        
        console.log('Mobile - Card game:', cardGame);
        console.log('Mobile - User enabled games:', enabledGames);
        
        if (!enabledGames.includes(cardGame)) {
          console.log(`Mobile - Game ${cardGame} is not enabled for user`);
          setError(`Sorry, we are not currently purchasing ${cardGame} cards. Please contact us if you have questions about our current buying list.`);
          setRecognizedCards([]);
          return;
        }
        
        // Game is enabled, proceed with displaying the card
        setRecognizedCards(catalogData.items);
        
        // Calculate prices for all recognized cards
        for (const card of catalogData.items) {
          if (card.skus && card.skus.length > 0) {
            const defaultCondition = allowedConditions && allowedConditions.length > 0 ? allowedConditions[0] : 'NM';
            const defaultPrinting = card.skus[0]?.printingName || 'Regular';
            const defaultLanguage = 'EN';
            
            
            // Set default selections
            setSelectedConditions(prev => ({ ...prev, [card._id]: defaultCondition }));
            setSelectedPrintings(prev => ({ ...prev, [card._id]: defaultPrinting }));
            setSelectedLanguages(prev => ({ ...prev, [card._id]: defaultLanguage }));
            await updateCardPrice(card, defaultCondition, defaultPrinting, defaultLanguage);
          }
        }
        
        console.log('Mobile card recognition completed - found card');
      } else {
        console.log('Mobile card recognition: No matching cards found');
        setError('Card not found in our catalog. Please try again.');
        setRecognizedCards([]);
      }
    } catch (error) {
      console.error('Error searching for card by productId:', error);
      setError('Failed to search for card. Please try again.');
      setRecognizedCards([]);
    }
  }, [updateCardPrice, allowedConditions, userSettings]);

  // Add recognized card to cart - EXACT same logic as manual search
  const handleAddToCart = useCallback((card, isCash) => {
    const condition = selectedConditions[card._id] || 'NM';
    const printing = selectedPrintings[card._id] || (card.skus && card.skus[0] ? card.skus[0].printingName : 'Regular');
    const language = selectedLanguages[card._id] || 'EN';
    const quantity = 1;
    
    // Get the price data for the selected language
    const cardPriceData = cardPrices[card._id];
    if (!cardPriceData) {
      console.error('Price not found for card:', card._id);
      return;
    }

    const currentPrice = cardPriceData;
    const basePrice = currentPrice.basePrice;
    const offeredPrice = isCash ? currentPrice.cashPrice : currentPrice.creditPrice;
    
    // For non-English cards, we need to find the equivalent EN SKU
    // but add a note that it's actually a non-English card
    let sku;
    let orderNote = '';
    
    if (language !== 'EN') {
      // First try to find the exact SKU with the selected language
      const exactSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
      
      // Then find the equivalent EN SKU
      const enSku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === 'EN'
      );
      
      // If we have an EN SKU, use it but add a note
      if (enSku) {
        sku = enSku;
        // Include the percentage in the note if it's a derived price
        if (currentPrice.isDerivedPrice) {
          orderNote = `This ${card.name} is actually a ${language} language card (${currentPrice.derivedPercentage}% of EN price).`;
        } else {
          orderNote = `This ${card.name} is actually a ${language} language card.`;
        }
      } else {
        // If no EN SKU exists, use the exact language SKU if available
        sku = exactSku;
      }
    } else {
      // For English cards, just find the matching SKU
      sku = card.skus.find(s => 
        s.condAbbr === condition && 
        s.printingName === printing && 
        s.langAbbr === language
      );
    }

    console.log('Adding to cart from mobile card recognition:', {
      name: card.name,
      condition,
      printing,
      language,
      basePrice,
      offeredPrice,
      isCash,
      skuId: sku?.skuId,
      isDerivedPrice: currentPrice.isDerivedPrice,
      derivedPercentage: currentPrice.derivedPercentage,
      orderNote
    });

    // Create a nonEnglish field for non-English cards
    let nonEnglish = null;
    if (language !== 'EN') {
      nonEnglish = {
        language,
        offeredPrice,
        isDerivedPrice: currentPrice.isDerivedPrice,
        derivedPercentage: currentPrice.derivedPercentage,
        basePrice
      };
    }

    // Pass the actual selected language, order note, and nonEnglish data to addToCart
    // Use EXACT same signature as manual search
    addToCart(card, condition, printing, quantity, sku?.skuId, isCash, language, orderNote, nonEnglish);
  }, [addToCart, cardPrices, selectedConditions, selectedPrintings, selectedLanguages]);

  // Clear results and start over
  const clearResults = useCallback(() => {
    setRecognizedCards([]);
    setCapturedImage(null);
    setError(null);
    priceRequests.current = {};
    setCardPrices({});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$', CHF: 'CHF', CNY: '¥', SEK: 'kr', NZD: 'NZ$',
    MXN: 'Mex$', SGD: 'S$', HKD: 'HK$', NOK: 'kr', KRW: '₩', TRY: '₺', RUB: '₽', INR: '₹', BRL: 'R$', ZAR: 'R', AED: 'د.إ'
  };

  const userCurrency = userSettings?.currency || 'USD';
  const currencySymbol = currencySymbols[userCurrency] || '$';

  return (
    <div className="mobile-card-recognition">
      {/* Instructions Section - Disappears on first interaction */}
      {showInstructions && !cameraActive && !capturedImage && !isLoading && recognizedCards.length === 0 && !error && (
        <div className={`mobile-instructions ${!showInstructions ? 'mobile-instructions-hidden' : ''}`}>
          <div className="mobile-instructions-content">
            <div className="mobile-instructions-icon">📱</div>
            <h3 className="mobile-instructions-title">{t('Scan My Cards')}</h3>
            <div className="mobile-instructions-steps">
              <div className="mobile-instruction-step">
                <div className="mobile-step-icon">📷</div>
                <p>{t('Use your camera to capture a clear photo of your trading card')}</p>
              </div>
              <div className="mobile-instruction-step">
                <div className="mobile-step-icon">📁</div>
                <p>{t('Or upload an existing image from your device')}</p>
              </div>
              <div className="mobile-instruction-step">
                <div className="mobile-step-icon">⚡</div>
                <p>{t('Get instant pricing and add cards to your buylist')}</p>
              </div>
            </div>
            <div className="mobile-instructions-tip">
              <p>{t('💡 Tip: Ensure good lighting and focus for best results')}</p>
            </div>
          </div>
        </div>
      )}



      {/* Camera Video */}
      {console.log('Mobile: Rendering: cameraActive =', cameraActive)}
      {cameraActive && (
        <div className="mobile-camera-container">
          {console.log('Mobile: Rendering camera container')}
          <div className="mobile-camera-viewport">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="mobile-camera-video"
              onLoadedMetadata={() => {
                console.log('Mobile: Video metadata loaded, video dimensions:', videoRef.current?.videoWidth, 'x', videoRef.current?.videoHeight);
              }}
              onError={(e) => {
                console.error('Mobile: Video error:', e);
              }}
              onCanPlay={() => {
                console.log('Mobile: Video can play');
              }}
            />

            {/* Card Guide Overlay */}
            <div className="mobile-card-guide-overlay">
              <div className="mobile-card-guide-frame">
                <div className="mobile-guide-corner mobile-guide-tl"></div>
                <div className="mobile-guide-corner mobile-guide-tr"></div>
                <div className="mobile-guide-corner mobile-guide-bl"></div>
                <div className="mobile-guide-corner mobile-guide-br"></div>
              </div>
              <div className="mobile-guide-text">
                {t('Align card within the frame')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Captured Image Preview - Only show if no recognized cards yet */}
      {capturedImage && recognizedCards.length === 0 && !isLoading && (
        <div className="mobile-captured-image-container">
          <h3>{t('Captured Image')}</h3>
          <img src={capturedImage} alt="Captured card" className="mobile-captured-image" />
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="mobile-recognition-loading">
          <div className="mobile-spinner"></div>
          <p>{t('Recognizing cards...')}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mobile-recognition-error">
          <div className="mobile-error-icon">❌</div>
          <h3>{t('Recognition Failed')}</h3>
          <p>{error}</p>
          <div className="mobile-error-suggestions">
            <h4>{t('Tips for better recognition:')}</h4>
            <ul>
              <li>{t('Ensure good lighting')}</li>
              <li>{t('Hold camera steady')}</li>
              <li>{t('Make sure card fills the frame')}</li>
              <li>{t('Avoid glare and shadows')}</li>
            </ul>
          </div>
        </div>
      )}

      {/* Recognized Cards - Improved Mobile UI */}
      {recognizedCards.length > 0 && (
        <div className="mobile-recognized-cards">
          <div className="mobile-card-match">
            {recognizedCards.map((card, index) => {
              const priceData = cardPrices[card._id] || { cashPrice: 0, creditPrice: 0, contactForPrice: true };
              
              return (
                <div key={index} className="mobile-card-match-container">
                  <div className="mobile-match-header">
                    <h3>{t('Card Found!')}</h3>
                  </div>
                  
                  <div className="mobile-recognized-card">
                    <div className="mobile-card-header">
                      <img
                        src={card.image || "/No-Image-Placeholder.svg.png"}
                        alt={card.name}
                        className="mobile-recognized-card-image"
                      />
                      <div className="mobile-recognized-card-info">
                        <h4>{card.name}</h4>
                        <p className="mobile-card-set">{card.expansionName}</p>
                        <p className="mobile-card-number">#{card.number}</p>
                        <p className="mobile-card-confidence">
                          {t('Confidence')}: {(card.confidence * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>

                    {/* Condition and Printing Selectors */}
                      {card.skus && card.skus.length > 0 && (
                        <div className="mobile-card-selectors">
                          <div className="mobile-selector-group">
                            <label>{t('Condition')}:</label>
                            <select
                              value={selectedConditions[card._id] || 'NM'}
                              onChange={(e) => {
                                const newCondition = e.target.value;
                                setSelectedConditions(prev => ({
                                  ...prev,
                                  [card._id]: newCondition
                                }));
                                updateCardPrice(card, newCondition, selectedPrintings[card._id] || card.skus[0]?.printingName, selectedLanguages[card._id] || 'EN');
                              }}
                            >
                              {(() => {
                                const allConditions = [...new Set(card.skus.map(sku => sku.condAbbr))];
                                const filteredConditions = allowedConditions && allowedConditions.length > 0
                                  ? allConditions.filter(condition => allowedConditions.includes(condition))
                                  : allConditions;
                                return filteredConditions.map(condition => (
                                  <option key={condition} value={condition}>
                                    {condition}
                                  </option>
                                ));
                              })()}
                            </select>
                          </div>

                          <div className="mobile-selector-group">
                            <label>{t('Printing')}:</label>
                            <select
                              value={selectedPrintings[card._id] || card.skus[0]?.printingName}
                              onChange={(e) => {
                                const newPrinting = e.target.value;
                                setSelectedPrintings(prev => ({
                                  ...prev,
                                  [card._id]: newPrinting
                                }));
                                updateCardPrice(card, selectedConditions[card._id] || 'NM', newPrinting, selectedLanguages[card._id] || 'EN');
                              }}
                            >
                              {[...new Set(card.skus.map(sku => sku.printingName))].map(printing => (
                                <option key={printing} value={printing}>
                                  {printing}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mobile-selector-group">
                            <label>{t('Language')}:</label>
                            <select
                              value={selectedLanguages[card._id] || 'EN'}
                              onChange={(e) => {
                                const newLanguage = e.target.value;
                                setSelectedLanguages(prev => ({
                                  ...prev,
                                  [card._id]: newLanguage
                                }));
                                updateCardPrice(card, selectedConditions[card._id] || 'NM', selectedPrintings[card._id] || card.skus[0]?.printingName, newLanguage);
                              }}
                            >
                              {[...new Set(card.skus.map(sku => sku.langAbbr))].map(language => (
                                <option key={language} value={language}>
                                  {language}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      <div className="mobile-card-actions">
                        {priceData.contactForPrice ? (
                          <button 
                            className="mobile-add-to-cart-button mobile-contact-for-price"
                            onClick={() => {
                              console.log('Contact for price requested for:', card.name);
                            }}
                          >
                            {t('Contact for Price')}
                          </button>
                        ) : priceData.isBulk ? (
                          <button className="mobile-add-to-cart-button mobile-bulk">
                            {t('Bulk Item')}
                          </button>
                        ) : (
                          <>
                            {priceData.creditPrice > 0 && (
                              <button 
                                className="mobile-add-to-cart-button mobile-credit"
                                onClick={() => handleAddToCart(card, false)}
                              >
                                {t('Credit')} {currencySymbol}{(priceData.creditPrice || 0).toFixed(2)}
                                {priceData.isDerivedPrice && (
                                  <span className="mobile-derived-price-indicator" title={`${t('Price calculated as')} ${priceData.derivedPercentage}% ${t('of EN price')}`}>*</span>
                                )}
                              </button>
                            )}
                            {priceData.cashPrice > 0 ? (
                              <button 
                                className="mobile-add-to-cart-button mobile-cash"
                                onClick={() => handleAddToCart(card, true)}
                              >
                                {t('Cash')} {currencySymbol}{(priceData.cashPrice || 0).toFixed(2)}
                                {priceData.isDerivedPrice && (
                                  <span className="mobile-derived-price-indicator" title={`${t('Price calculated as')} ${priceData.derivedPercentage}% ${t('of EN price')}`}>*</span>
                                )}
                              </button>
                            ) : (
                              <button className="mobile-add-to-cart-button mobile-not-buying" disabled>
                                {t('Not Buying Cash')}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Controls - Ergonomic positioning for mobile */}
      <div className="mobile-bottom-controls">
        {!cameraActive ? (
          <div className="mobile-main-controls">
            <button
              className="mobile-btn mobile-btn-primary mobile-btn-large"
              onClick={startCamera}
              disabled={isLoading}
            >
              📷 {t('Start Camera')}
            </button>
            <label className="mobile-btn mobile-btn-outline mobile-btn-large">
              📁 {t('Upload Image')}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={isLoading}
              />
            </label>
          </div>
        ) : (
          <div className="mobile-camera-active-controls">
            <button
              className="mobile-btn mobile-btn-success mobile-btn-large"
              onClick={capturePhoto}
              disabled={isLoading}
            >
              📸 {t('Capture Photo')}
            </button>
          </div>
        )}

        {(recognizedCards.length > 0 || capturedImage) && (
          <button
            className="mobile-btn mobile-btn-outline mobile-btn-large"
            onClick={clearResults}
            disabled={isLoading}
          >
            ➕ {t('Add Another')}
          </button>
        )}
      </div>
    </div>
  );
};

export default MobileCardRecognition;
